import { memo, useCallback, useMemo, useState } from "react";

import { Typography } from "../typography";
import { Badge } from "../badge";

import styles from "./styles.module.scss";
import type { IAvatarProps, IAvatarSize, IUserNameData } from "./types";
import { getSize } from "./getSize";

import type { IFontSizeType } from "@constants";

// It is possible that the URL that is sent to us
// is not valid. In this function, we check whether the
// URL is a valid or not
const isValidUrl = (url: string) => {
  try {
    const _url = new URL(url);

    return !!_url;
  } catch {
    return false;
  }
};

const getUppercaseFirst = (text: string) => {
  return text?.charAt(0)?.toUpperCase();
};

const getAvatarName = ({ firstName, lastName }: IUserNameData) => {
  if (firstName && lastName)
    return `${getUppercaseFirst(firstName)}.${getUppercaseFirst(lastName)}`;

  return getUppercaseFirst(lastName) || getUppercaseFirst(firstName) || "";
};

const avatarFontSizes: Record<IAvatarSize, IFontSizeType> = {
  "very-small": "base",
  small: "base",
  medium: "xl",
  large: "2xl",
};

/**
 * Avatar Component
 *
 * Displays an avatar image or the initials
 *  of the user if the image URL is invalid.
 *
 */
export const Avatar = memo(function Avatar(props: IAvatarProps) {
  const { userInfo, size, variant, role, sidebar, roleValue } = props;

  const avatarSize = useMemo(() => getSize(size), [size]);
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // According to whether the URL is available or not,
  // or if it is valid or not, we will display the avatar
  // or initials's of the user
  const renderAvatarContent = useCallback(() => {
    if (userInfo.imageUrl && isValidUrl(userInfo.imageUrl) && !imageError) {
      return (
        <img
          src={userInfo.imageUrl}
          alt={"Avatar"}
          className={styles.avatar}
          onError={handleImageError}
        />
      );
    } else {
      const initials = getAvatarName(userInfo);

      return (
        <span>
          <Typography
            center
            color={"primary-dark"}
            fontFamily={"Morabba"}
            fontSize={avatarFontSizes[size]}
            fontWeight={"semi-bold"}
          >
            {initials}
          </Typography>
        </span>
      );
    }
  }, [handleImageError, imageError, size, userInfo]);

  return (
    <div className={styles.wrapper}>
      {role && roleValue && (
        <>
          {sidebar && variant && (
            <div className={styles.border}>
              <div className={styles.borderInside} />
              <div className={styles.segment} />
            </div>
          )}
          <span className={styles.badgeContainer}>
            <Badge role={role} value={roleValue} />
          </span>
        </>
      )}
      <div
        className={`${styles.avatarContainer} ${avatarSize}`}
        style={
          variant
            ? { width: `${variant / 16}rem`, height: `${variant / 16}rem` }
            : {}
        }
      >
        {renderAvatarContent()}
      </div>
    </div>
  );
});
